// Biblioteca de textos de disparo no editor: textos prontos do LucraLink e os
// salvos pelo usuario. Ao inserir, as variaveis ({title}, {price}...) sao
// preenchidas com o link escolhido pelo mesmo renderizador do encaminhamento.

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { TextQuote } from "lucide-react";
import { BROADCAST_TEXT_PRESETS, renderBroadcastText, type BroadcastLinkData } from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { useBroadcastTexts } from "@/lib/hooks/useBroadcastTexts";
import { cn } from "@/lib/utils";

interface BroadcastTextPickerProps {
  /** Dados do link para preencher as variaveis (null = sem link escolhido). */
  readonly link: BroadcastLinkData | null;
  readonly onInsert: (text: string) => void;
}

const PRESET_PREFIX = "preset:";

/** Corpo do texto escolhido (pronto ou salvo). null = nada escolhido. */
export function bodyForChoice(choice: string, saved: ReadonlyArray<{ id: string; body: string }>): string | null {
  if (choice.startsWith(PRESET_PREFIX)) {
    return BROADCAST_TEXT_PRESETS.find((p) => p.key === choice.slice(PRESET_PREFIX.length))?.body ?? null;
  }
  return saved.find((t) => t.id === choice)?.body ?? null;
}

export function BroadcastTextPicker({ link, onInsert }: BroadcastTextPickerProps): JSX.Element {
  const { data: saved = [], isError } = useBroadcastTexts();
  const [choice, setChoice] = useState("");

  function insert(): void {
    const body = bodyForChoice(choice, saved);
    if (!body) return;
    onInsert(renderBroadcastText(body, link));
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <label htmlFor="broadcast-text" className="font-medium">
          Textos de disparo
        </label>
        <Link to="/app/textos-de-disparo" className="text-[11px] text-primary hover:underline">
          Gerenciar textos
        </Link>
      </div>
      <div className="flex gap-2">
        <select
          id="broadcast-text"
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          className={cn(
            "h-9 min-w-0 flex-1 rounded-md border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface-raised))] px-3 text-sm text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <option value="">Escolha um texto</option>
          <optgroup label="Prontos">
            {BROADCAST_TEXT_PRESETS.map((p) => (
              <option key={p.key} value={`${PRESET_PREFIX}${p.key}`}>
                {p.name}
              </option>
            ))}
          </optgroup>
          {saved.length > 0 && (
            <optgroup label="Meus textos">
              {saved.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        <Button type="button" variant="outline" size="sm" className="h-9" disabled={!choice} onClick={insert}>
          <TextQuote size={13} /> Inserir
        </Button>
      </div>
      <span className="text-[11px] text-muted-foreground">
        {isError
          ? "Não foi possível carregar seus textos salvos; os prontos continuam disponíveis."
          : link
            ? "As variáveis são preenchidas com o link escolhido abaixo."
            : "Sem link escolhido, as linhas com variáveis ficam de fora."}
      </span>
    </div>
  );
}
